import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import LinkTranslated from '@components/shared/LinkTranslated';
import { VITALS_URL } from '@constants';
import { Article, CategoryNode } from '@contracts';

interface Props {
  article: Article;
  category: CategoryNode;
}

export default function ArticleHeader({ article, category }: Props) {
  const { first_publication_date } = article;
  const { title, featured_image, article_subheadline, category: articleCategory } = article.data;
  const { title: categoryName, image_background_color, tag_name_color } = category.data;

  const publicationDate = new Date(first_publication_date).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <HeaderContainer>
      <ImageContainer $imageBackground={image_background_color}>
        <GatsbyImage image={featured_image.gatsbyImageData} alt={featured_image.alt ?? title} loading="eager" />
      </ImageContainer>
      <TextContainer>
        <ArticleCategoryContainer>
          <LinkTranslated to={`${VITALS_URL}${articleCategory.uid}/`}>
            <ArticleCategory $variant={TextVariant.UpperText5} $tagNameColor={tag_name_color}>
              {categoryName}
            </ArticleCategory>
          </LinkTranslated>
        </ArticleCategoryContainer>
        <TitleContainer>
          <ArticleTitle $variant={TextVariant.Heading3} $breakpoint={1024} $mobileVariant={TextVariant.Heading6}>
            {title}
          </ArticleTitle>
          <ArticleSubheadline $variant={TextVariant.BodyRegularLG}>{article_subheadline}</ArticleSubheadline>
        </TitleContainer>
        <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
          {publicationDate}
        </Typography>
      </TextContainer>
    </HeaderContainer>
  );
}

interface ImageContainerProps {
  $imageBackground: string;
}
interface ArticleCategoryProps {
  $tagNameColor: string;
}

const HeaderContainer = styled.div`
  margin: 0;
  display: flex;
  height: auto;
  width: 100%;
  text-align: center;
  align-items: stretch;
  position: relative;
  max-width: 100%;

  @media only screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const ImageContainer = styled.div<ImageContainerProps>`
  display: flex;
  flex-direction: row-reverse;
  flex: 1 1 auto;
  width: 50%;
  background-color: ${({ $imageBackground }) => $imageBackground ?? 'inherit'};

  @media only screen and (max-width: 768px) {
    width: 100%;
    justify-content: center;
    align-items: center;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 50%;
  padding: 72px 0;
  color: ${Colors.navyBlue};

  @media only screen and (max-width: 1024px) {
    padding: 56px 32px;
  }

  @media only screen and (max-width: 768px) {
    width: 100%;
    padding: 56px 16px;
    max-width: 430px;
    min-height: 430px;
  }
`;

const ArticleCategoryContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ArticleCategory = styled(Typography)<ArticleCategoryProps>`
  padding: 3px 5px;
  width: fit-content;
  height: fit-content;

  border-radius: 2px;
  background: ${({ $tagNameColor }) => $tagNameColor ?? Colors.ecru};
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 1200px) {
    margin: 40px 0;
  }

  @media only screen and (max-width: 768px) {
    margin: 40px 22px;
  }
`;

const ArticleTitle = styled(Typography)`
  text-transform: capitalize;
  text-shadow: 0px 4px 30px rgba(0, 0, 0, 0.05);
  width: 72%;
  margin: 0 auto 24px;

  @media only screen and (max-width: 768px) {
    margin: 0;
    margin-bottom: 20px;
    width: 100%;
  }
`;

const ArticleSubheadline = styled(Typography)`
  text-shadow: 0px 4px 30px rgba(0, 0, 0, 0.05);

  width: 72%;
  margin: 0 auto;

  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;
